import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatableApp } from '@vendasta/marketplace-packages';
import {
  ListSubstitutionParametersRequestFiltersInterface,
  SubstitutionParameter,
  TemplatesService,
} from '@vendasta/templates';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  AppListingServiceInterface,
  EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN,
} from '../../services/app-listing-service.interface';
import { CrmDynamicFieldService } from '../../services/crm-dynamic-field.service';

export interface Option {
  title: string;
  value: string;
}

export interface VariableMenuItemOptions {
  requiredParams: string[];
  nestedDynamicSelector: boolean;
}

export interface VariableMenuItem {
  textKey: string;
  replacementTags?: string[];
  replacementTag?: string;
  template?: string;
  subItems?: SubstitutionParameter[];
  options?: VariableMenuItemOptions;
}

@Injectable()
export class DynamicComponentSelectorService {
  private parentVariableCategories: VariableMenuItem[] = [];
  private initialCategories: VariableMenuItem[] = [];
  constructor(
    private readonly templatesService: TemplatesService,
    @Inject(EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN) @Optional() private appListingService: AppListingServiceInterface,
    private readonly crmDynamicFieldService: CrmDynamicFieldService,
  ) {}

  public hasComponentsToDisplay(): boolean {
    return this.parentVariableCategories.length > 0;
  }

  public getVariables(filters: ListSubstitutionParametersRequestFiltersInterface): Observable<VariableMenuItem[]> {
    return this.templatesService
      .listSubstitutionParameters(filters)
      .pipe(map((params) => this.processCategories(params)));
  }

  private processCategories(params: SubstitutionParameter[]): VariableMenuItem[] {
    const initialCategories = this.getInitialCategories();
    const categories = this.filterSubstitutionParameters(params, initialCategories);
    return this.addCrmSubItems(categories);
  }

  private filterSubstitutionParameters(
    params: SubstitutionParameter[],
    categories: VariableMenuItem[],
  ): VariableMenuItem[] {
    return categories.map((v) => ({
      textKey: v.textKey,
      replacementTags: v.replacementTags,
      options: v.options,
      subItems: params
        .filter((p) => v.replacementTags.some((t) => t === p.replacementTag))
        .sort(function (a, b): number {
          return v.replacementTags.indexOf(a.replacementTag) - v.replacementTags.indexOf(b.replacementTag);
        }),
    }));
  }

  private initializeCategories(parentCategories: VariableMenuItem[]): void {
    if (!this.initialCategories.length) {
      this.initialCategories = JSON.parse(JSON.stringify(parentCategories));
    }
  }

  private addCrmSubItems(parentCategories: VariableMenuItem[]): VariableMenuItem[] {
    const processFields = (
      parentCategories: VariableMenuItem[],
      textKey: string,
      fields$: Observable<VariableMenuItem[]>,
    ) => {
      fields$.pipe(take(1)).subscribe((fields) => {
        const category = parentCategories.find((v) => v.textKey === textKey);
        if (!category || !category.options?.nestedDynamicSelector) {
          return;
        }

        category.subItems = fields.map((field) => {
          return {
            replacementTag: field.replacementTag,
            displayName: field.textKey,
          } as SubstitutionParameter;
        });

        category.options.nestedDynamicSelector = false;
      });
    };

    const companyTextKey = 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.PRIMARY_COMPANY_DETAILS';
    const contactTextKey = 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.CONTACT_DETAILS';

    processFields(parentCategories, companyTextKey, this.getCRMPrimaryCompanyFields$());
    processFields(parentCategories, contactTextKey, this.getCRMContactFields$());

    return parentCategories;
  }

  public getProductSubstitutionVariables(): Observable<ActivatableApp[]> {
    return this.appListingService.listAllActivatableApps().pipe(take(1));
  }

  public overrideParentVariableCategories(variableCategories: VariableMenuItem[]): VariableMenuItem[] {
    this.initializeCategories(variableCategories);
    return (this.parentVariableCategories = variableCategories);
  }

  public getCRMContactFields$(): Observable<VariableMenuItem[]> {
    return this.crmDynamicFieldService.contactFields$.pipe(
      map((fieldsMap) => {
        return Object.values(fieldsMap).map(
          (field): VariableMenuItem => ({
            textKey: field.displayName,
            replacementTag: field.templateReplacementTag,
          }),
        );
      }),
      map((items: VariableMenuItem[]) => items.sort((a, b) => a.textKey.localeCompare(b.textKey))),
    );
  }

  public getCRMPrimaryCompanyFields$(): Observable<VariableMenuItem[]> {
    return this.crmDynamicFieldService.associatedCompanyFields$.pipe(
      map((fieldsMap) => {
        return Object.values(fieldsMap).map(
          (field): VariableMenuItem => ({
            textKey: field.displayName,
            replacementTag: field.templateReplacementTag,
          }),
        );
      }),
      map((items: VariableMenuItem[]) => items.sort((a, b) => a.textKey.localeCompare(b.textKey))),
    );
  }

  private getInitialCategories(): VariableMenuItem[] {
    return JSON.parse(JSON.stringify(this.initialCategories));
  }
}
